import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { AccountShell } from "@/components/customer/AccountShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useCustomerProfile } from "@/hooks/useCustomerAccount";
import { useCustomerSession } from "@/hooks/useCustomerSession";

export const Route = createFileRoute("/_customer/account/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { user } = useCustomerSession();
  const { profile, isLoading, save } = useCustomerProfile();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name ?? "");
    setPhone(profile.phone ?? "");
    setMarketing(profile.marketing_opt_in);
  }, [profile?.id, profile?.updated_at]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync({
        full_name: fullName.trim() || null,
        phone: phone.trim() || null,
        marketing_opt_in: marketing,
      });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update your profile");
    }
  }

  return (
    <AccountShell title="Profile" description="Your name and contact details.">
      <form
        onSubmit={onSubmit}
        className="max-w-lg space-y-5 rounded-xl border border-border/60 bg-card p-5 md:p-6"
        noValidate
      >
        <div className="space-y-2">
          <Label htmlFor="profile-name">Full name</Label>
          <Input
            id="profile-name"
            value={fullName}
            autoComplete="name"
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-email">Email</Label>
          <Input id="profile-email" value={profile?.email ?? user?.email ?? ""} disabled />
          <p className="text-xs text-muted-foreground">
            Email is managed by your sign-in and can't be edited here.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-phone">Phone</Label>
          <Input
            id="profile-phone"
            value={phone}
            inputMode="tel"
            autoComplete="tel"
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="profile-marketing" className="text-sm font-normal">
            Send me occasional menu updates
          </Label>
          <Switch id="profile-marketing" checked={marketing} onCheckedChange={setMarketing} />
        </div>
        <Button type="submit" disabled={isLoading || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </Button>
      </form>
    </AccountShell>
  );
}
